import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cartService } from '@/services/cart';
import type {
  AddToCartResult,
  RemoveFromCartResult,
  ClearCartResult,
  GetCartResult,
  WorkflowType,
} from '@/services/cart';
import { useNotifications } from '@/stores/useNotificationStore';

export type MaintenanceWorkflowType = Extract<
  WorkflowType,
  'maintenance-send' | 'maintenance-receive'
>;

const DEFAULT_WORKFLOW_TYPE: MaintenanceWorkflowType = 'maintenance-send';

/**
 * Query key factory for maintenance cart queries
 */
export const maintenanceCartQueryKeys = {
  all: ['maintenance-cart'] as const,
  cart: (userId: string | null | undefined, workflowType: MaintenanceWorkflowType) =>
    [...maintenanceCartQueryKeys.all, 'cart', workflowType, userId ?? 'no-user'] as const,
};

/**
 * Hook to fetch the current user's maintenance cart.
 * Returns cart metadata and reserved asset entities.
 */
export const useMaintenanceCart = (
  userId: string | null | undefined,
  workflowType: MaintenanceWorkflowType = DEFAULT_WORKFLOW_TYPE,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery<GetCartResult>({
    queryKey: maintenanceCartQueryKeys.cart(userId, workflowType),
    queryFn: () => cartService.getCart(userId!, workflowType),
    enabled: options?.enabled !== false && !!userId,
    staleTime: 10_000, // 10 seconds
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to add assets to the maintenance cart.
 * Creates cart as needed and reserves assets in the database.
 */
export const useAddToMaintenanceCart = (
  workflowType: MaintenanceWorkflowType = DEFAULT_WORKFLOW_TYPE,
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<AddToCartResult, Error, { userId: string; assetIds: string[] }>({
    mutationFn: (params) =>
      cartService.addToCart({
        customerId: params.userId,
        assetIds: params.assetIds,
        itemSource: 'additional', // Maintenance doesn't use IE Kit
        workflowType,
      }),
    onSuccess: (result, variables) => {
      if (!result.success) {
        if (result.already_reserved && result.already_reserved.length > 0) {
          notifications.warning(
            'Assets unavailable',
            `${result.already_reserved.length} asset(s) are already reserved by another user or workflow.`,
          );
        } else {
          notifications.error('Failed to add to cart', result.error ?? 'Unknown error');
        }
        return;
      }

      // Invalidate maintenance cart to refresh UI
      void queryClient.invalidateQueries({
        queryKey: maintenanceCartQueryKeys.cart(variables.userId, workflowType),
      });

      // Invalidate asset inventory since we just reserved some
      void queryClient.invalidateQueries({ queryKey: ['asset-inventory'] });
    },
    onError: (error) => {
      notifications.error('Failed to add to cart', error.message);
    },
  });
};

/**
 * Hook to remove assets from the maintenance cart.
 * Releases reservations for removed assets.
 */
export const useRemoveFromMaintenanceCart = (
  workflowType: MaintenanceWorkflowType = DEFAULT_WORKFLOW_TYPE,
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<RemoveFromCartResult, Error, { userId: string; assetIds: string[] }>({
    mutationFn: (params) =>
      cartService.removeFromCart({
        customerId: params.userId,
        assetIds: params.assetIds,
        workflowType,
      }),
    onSuccess: async (result, variables) => {
      if (!result.success) {
        notifications.error('Failed to remove from cart', result.error ?? 'Unknown error');
        return;
      }

      // Invalidate maintenance cart to refresh UI
      await queryClient.invalidateQueries({
        queryKey: maintenanceCartQueryKeys.cart(variables.userId, workflowType),
      });

      // Invalidate asset inventory since we released some
      void queryClient.invalidateQueries({ queryKey: ['asset-inventory'] });
    },
    onError: (error) => {
      notifications.error('Failed to remove from cart', error.message);
    },
  });
};

/**
 * Hook to clear the entire maintenance cart, releasing all reservations.
 */
export const useClearMaintenanceCart = (
  workflowType: MaintenanceWorkflowType = DEFAULT_WORKFLOW_TYPE,
) => {
  const queryClient = useQueryClient();

  return useMutation<ClearCartResult, Error, { userId: string; cartId?: string; allowActiveScan?: boolean }>({
    mutationFn: async ({ userId, cartId, allowActiveScan }) => {
      const result = await cartService.clearCart(userId, workflowType, {
        allowActiveScan,
      });

      // Delete the cart entity if it exists
      if (result.success && cartId) {
        try {
          await cartService.deleteCart(cartId);
        } catch {
          // Cart cleanup is best-effort
        }
      }

      return result;
    },
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({
        queryKey: maintenanceCartQueryKeys.cart(variables.userId, workflowType),
      });

      // Invalidate asset inventory since we released reservations
      void queryClient.invalidateQueries({ queryKey: ['asset-inventory'] });
    },
  });
};
